import React from 'react';
import { Bot } from 'lucide-react';
import { LLMProvider, LLMModel } from '../../types';

interface SimpleLLMSelectorProps {
  selectedLLM: LLMProvider;
  selectedModel: string;
  availableModels: LLMModel[];
  onLLMChange: (provider: LLMProvider, model: string) => void;
}

const SimpleLLMSelector: React.FC<SimpleLLMSelectorProps> = ({
  selectedLLM,
  selectedModel,
  availableModels,
  onLLMChange
}) => {
  const handleProviderSelect = (provider: LLMProvider) => {
    const providerModels = availableModels.filter(m => m.provider === provider);
    if (providerModels.length > 0) {
      onLLMChange(provider, providerModels[0].id);
    }
  };

  const handleModelSelect = (modelId: string) => {
    const model = availableModels.find(m => m.id === modelId);
    if (model) {
      onLLMChange(model.provider, model.id);
    }
  };


  const getProviderName = (provider: LLMProvider) => {
    // Ищем первую модель провайдера и берем её providerDisplayName
    const model = availableModels.find(m => m.provider === provider);
    if (model?.providerDisplayName) {
      return model.providerDisplayName;
    }
    
    // Fallback на хардкод (если providerDisplayName не указан)
    switch (provider) {
      case 'yandex': return 'YandexGPT';
      case 'gigachat': return 'GigaChat';
      case 'perplexity': return 'Perplexity';
      default: return provider;
    }
  };

  const availableProviders = Array.from(new Set(availableModels.map(m => m.provider)));
  const currentProviderModels = availableModels.filter(m => m.provider === selectedLLM);

  const isMobile = window.innerWidth < 768;

  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: isMobile ? '0.5rem' : '1rem',
      padding: isMobile ? '0.75rem' : '1rem',
      marginBottom: isMobile ? '0.75rem' : '1.5rem',
      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
    }}>
      <h3 style={{
        margin: '0 0 0.75rem 0',
        fontSize: isMobile ? '0.95rem' : '1.125rem',
        fontWeight: '600',
        color: '#111827',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <Bot size={isMobile ? 18 : 20} />
        Выбор LLM модели
      </h3>

      {/* Выбор провайдера */}
      <div style={{ marginBottom: '1rem' }}>
        <label htmlFor="provider-select" style={{
          display: 'block',
          marginBottom: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          color: '#374151'
        }}>
          Провайдер:
        </label>
        <select
          id="provider-select"
          value={selectedLLM}
          onChange={(e) => handleProviderSelect(e.target.value as LLMProvider)}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            background: '#ffffff',
            color: '#111827',
            fontSize: '0.875rem',
            cursor: 'pointer',
            outline: 'none'
          }}
        >
          {availableProviders.map((provider) => (
            <option key={provider} value={provider}>
              {getProviderName(provider)} ({availableModels.filter(m => m.provider === provider).length})
            </option>
          ))}
        </select>
      </div>

      {/* Выбор модели */}
      {currentProviderModels.length > 0 && (
        <div>
          <label htmlFor="model-select" style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#374151'
          }}>
            Модель:
          </label>
          <select
            id="model-select"
            value={selectedModel}
            onChange={(e) => handleModelSelect(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              background: '#ffffff',
              color: '#111827',
              fontSize: '0.875rem',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            {currentProviderModels.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name} {model.description && `- ${model.description}`}
              </option>
            ))}
          </select>
          
          <div style={{
            marginTop: '0.5rem',
            fontSize: '0.75rem',
            color: '#6b7280',
            lineHeight: '1.4'
          }}>
            {currentProviderModels.find(m => m.id === selectedModel)?.description || 'Описание модели недоступно'}
          </div>
        </div>
      )}

      {/* Информация о выбранной модели */}
      {availableModels.length === 0 && (
        <div style={{
          padding: '1rem',
          background: '#f9fafb',
          borderRadius: '0.5rem',
          color: '#6b7280',
          fontSize: '0.875rem',
          textAlign: 'center'
        }}>
          Модели загружаются...
        </div>
      )}
    </div>
  );
};

export default SimpleLLMSelector;
